import { useState, useEffect, useRef, useCallback } from 'react'
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { IMAGE_SLOTS, type SlotKey } from '../slotRegistry'
import { designerService } from '../services/designer.service'
import type { MediaDocument } from '@/features/admin/services/admin.media.service'
import type { ImageSlotDocument, OverlayConfig } from '@/types'
import { MediaPicker } from './MediaPicker'
import { FocusPointPicker } from './FocusPointPicker'
import { OverlayPicker } from './OverlayPicker'

type Step = 'pick-media' | 'pick-focus' | 'pick-overlay'

const DEFAULT_OVERLAY: OverlayConfig = { type: 'none' }

interface DesignerSlidePanelProps {
  slotKey: SlotKey
  currentAssignment: ImageSlotDocument | null
  open: boolean
  onClose: () => void
}

export function DesignerSlidePanel({
  slotKey,
  currentAssignment,
  open,
  onClose,
}: DesignerSlidePanelProps) {
  const { user } = useAuth()
  const slot = IMAGE_SLOTS[slotKey]

  const [width, setWidth] = useState(480)
  const dragging = useRef(false)
  const startX = useRef(0)
  const startWidth = useRef(0)

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging.current) return
    const delta = startX.current - e.clientX
    setWidth(Math.min(900, Math.max(320, startWidth.current + delta)))
  }, [])

  const onMouseUp = useCallback(() => {
    dragging.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [onMouseMove, onMouseUp])

  const [step, setStep] = useState<Step>('pick-media')
  const [selectedMedia, setSelectedMedia] = useState<MediaDocument | null>(null)
  const [focusX, setFocusX] = useState(currentAssignment?.focusX ?? 0.5)
  const [focusY, setFocusY] = useState(currentAssignment?.focusY ?? 0.5)
  const [overlay, setOverlay] = useState<OverlayConfig>(currentAssignment?.overlay ?? DEFAULT_OVERLAY)
  const [saving, setSaving] = useState(false)

  function handleOpenChange(open: boolean) {
    if (!open) {
      onClose()
      // Reset state after close animation
      setTimeout(() => {
        setStep('pick-media')
        setSelectedMedia(null)
        setFocusX(currentAssignment?.focusX ?? 0.5)
        setFocusY(currentAssignment?.focusY ?? 0.5)
        setOverlay(currentAssignment?.overlay ?? DEFAULT_OVERLAY)
      }, 300)
    }
  }

  function handleSelectMedia(item: MediaDocument) {
    setSelectedMedia(item)
  }

  function handleNext() {
    if (!selectedMedia) return
    setFocusX(currentAssignment?.focusX ?? 0.5)
    setFocusY(currentAssignment?.focusY ?? 0.5)
    setStep('pick-focus')
  }

  function handleNextToOverlay() {
    setStep('pick-overlay')
  }

  async function handleAssign() {
    if (!selectedMedia || !user) return
    setSaving(true)
    try {
      await designerService.assignSlot(slotKey, selectedMedia, focusX, focusY, overlay, user.uid)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" style={{ width }} className="flex max-w-[90vw] flex-col gap-0 p-0 sm:max-w-[90vw]">
        {/* Drag handle */}
        <div
          onMouseDown={(e) => {
            dragging.current = true
            startX.current = e.clientX
            startWidth.current = width
            document.body.style.cursor = 'ew-resize'
            document.body.style.userSelect = 'none'
          }}
          className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize hover:bg-primary/20 transition-colors z-50"
        />
        <SheetHeader className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base">{slot.label}</SheetTitle>
            <span className="text-xs text-muted-foreground tabular-nums">
              Step {step === 'pick-media' ? '1' : step === 'pick-focus' ? '2' : '3'} / 3
            </span>
          </div>
          <SheetDescription className="text-xs">{slot.description}</SheetDescription>
        </SheetHeader>

        {step === 'pick-media' && (
          <>
            <div className="flex-1 min-h-0 overflow-hidden p-4">
              <MediaPicker
                selectedId={selectedMedia?.id ?? currentAssignment?.mediaId ?? null}
                onSelect={handleSelectMedia}
              />
            </div>
            <div className="border-t px-6 py-4 flex justify-end">
              <Button
                onClick={handleNext}
                disabled={!selectedMedia}
                className="gap-2"
              >
                Set focus point
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}

        {step === 'pick-focus' && (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedMedia && (
                <FocusPointPicker
                  imageUrl={selectedMedia.url}
                  focusX={focusX}
                  focusY={focusY}
                  onChange={(x, y) => { setFocusX(x); setFocusY(y) }}
                />
              )}
            </div>
            <div className="border-t px-6 py-4 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep('pick-media')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleNextToOverlay} className="gap-2">
                Set overlay
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}

        {step === 'pick-overlay' && (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedMedia && (
                <OverlayPicker
                  imageUrl={selectedMedia.url}
                  focusX={focusX}
                  focusY={focusY}
                  value={overlay}
                  onChange={setOverlay}
                />
              )}
            </div>
            <div className="border-t px-6 py-4 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep('pick-focus')}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={() => void handleAssign()} disabled={saving} className="gap-2">
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
                {saving ? 'Saving…' : 'Assign'}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
